import React, { ChangeEvent, FC, FocusEvent } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { FloatingLabelInput } from 'components/floating-label-fields';
import IconAmex from 'components/icons/amex';
import IconDinersClub from 'components/icons/diners-club';
import IconDiscover from 'components/icons/discover';
import IconJCB from 'components/icons/jcb';
import IconMastercard from 'components/icons/mastercard';
import IconVisa from 'components/icons/visa';
import { PaymentFieldVariations } from 'state/launchdarkly/variations';
import { CardType } from 'state/payment/types';
import { FormValidationState } from 'utils/form';
import { creditCardNumberMask, expiryMask } from 'utils/masks';
import { IPaymentErrors, IPaymentState } from 'utils/payment';

interface IFirstDataCreditCardFormInputs {
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onBlur: (event: FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  errors: IPaymentErrors;
  paymentValues: IPaymentState;
  supportedCardTypes?: CardType[];
  paymentFieldVariations: PaymentFieldVariations;
}

export const CardNumberWrapper = styled.div`
  flex-basis: 100%;
  flex-grow: 1;
  position: relative;
  ${Styles.desktop`
    flex-basis: 50%;
  `}
`;

const CvvWrapper = styled.div`
  flex-basis: 40%;
  margin-left: 2.5%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 20%;
  `}
`;

const ExpiryWrapper = styled.div`
  flex-basis: 40%;
  margin-right: 2.5%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 20%;
  `}
`;

const ExpiryCVVWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const AcceptedCards = styled.div`
  position: absolute;
  right: 1rem;
  top: 1.6rem;
  z-index: ${Styles.zIndex.max - 300};

  svg {
    height: 1.6rem;
    width: 1.6rem;
    margin-right: 0.5rem;
  }

  > :last-child {
    margin-right: 0;
  }
`;

const CreditCardIcon: FC<{ cardType: string }> = ({ cardType }) =>
  ({
    AMEX: <IconAmex />,
    DISCOVER: <IconDiscover />,
    DINERS_CLUB: <IconDinersClub />,
    JCB: <IconJCB />,
    MASTERCARD: <IconMastercard />,
    VISA: <IconVisa />,
  }[cardType] || null);

const FirstDataCreditCardInputs: FC<IFirstDataCreditCardFormInputs> = ({
  errors,
  onChange,
  onBlur,
  paymentValues,
  supportedCardTypes = [],
  paymentFieldVariations,
}) => {
  const { formatMessage } = useIntl();
  return (
    <>
      {paymentFieldVariations.cardNumber && (
        <CardNumberWrapper>
          <AcceptedCards>
            {paymentValues.isCardNumberValid ? (
              <CreditCardIcon cardType={paymentValues.cardType} />
            ) : (
              <>
                {supportedCardTypes.map(
                  // @TODO: Global team to uncomment in upcoming task
                  () => null // <CreditCardIcon key={cardType} cardType={cardType} />
                )}
              </>
            )}
          </AcceptedCards>
          <FloatingLabelInput
            label={formatMessage({ id: 'creditCardNumber' })}
            name="cardNumber"
            data-testid="cardNumber"
            aria-label={formatMessage({ id: 'creditCardNumber' })}
            type="text"
            inputMode="numeric"
            value={paymentValues.cardNumber}
            onChange={onChange}
            onBlur={onBlur}
            autocomplete="cc-number"
            validation={
              errors.cardType || errors.cardNumber ? FormValidationState.INVALID : undefined
            }
            message={errors.cardType || errors.cardNumber}
            required
            mask={creditCardNumberMask}
            guide
            placeholderChar=" "
          />
        </CardNumberWrapper>
      )}
      <ExpiryCVVWrapper>
        {paymentFieldVariations.expiration && (
          <ExpiryWrapper>
            <FloatingLabelInput
              label={formatMessage({ id: 'monthYearShort' })}
              name="expiry"
              data-testid="expiry"
              aria-label={formatMessage({ id: 'cardExpiration' })}
              type="text"
              inputMode="numeric"
              value={paymentValues.expiry}
              onChange={onChange}
              onBlur={onBlur}
              required
              autocomplete="cc-exp"
              validation={errors.expiry ? FormValidationState.INVALID : undefined}
              message={errors.expiry}
              mask={expiryMask}
              guide={false}
            />
          </ExpiryWrapper>
        )}
        {paymentFieldVariations.cvv && (
          <CvvWrapper>
            <FloatingLabelInput
              label={formatMessage({ id: 'cvv' })}
              name="cvv"
              data-testid="cvv"
              aria-label={formatMessage({ id: 'cvv' })}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={paymentValues.cvv}
              onChange={onChange}
              onBlur={onBlur}
              maxLength="4"
              required
              autocomplete="cc-csc"
              validation={errors.cvv ? FormValidationState.INVALID : undefined}
              message={errors.cvv}
            />
          </CvvWrapper>
        )}
      </ExpiryCVVWrapper>
    </>
  );
};

export default FirstDataCreditCardInputs;
